import React, { useMemo, useState, useEffect } from 'react';
import useFetchCompanyUsers from 'App/hooks/useFetchCompanyUsers';
import BranchesCell from './BranchesCell';
import RolesCell from './RolesCell';
import * as R from 'ramda';
import { debounce } from 'lodash';
import UserActionsCell from './UserActionsCell';
import hasLength from 'App/utils/hasLength';
import propOrDash from 'App/utils/propOrDash';
import createUserLinkCell from '../../components/UserLinkCell/createUserLinkCell';
import { USER_LINK_PATHS } from '../../constants';
import ManualPageTable from 'App/components/ManualPageTable/ManualPageTable';
import SearchColumnFilterManual from 'App/components/ManualPageTable/SearchColumnFilterManual';

const propTypes = {};

const defaultProps = {};

function UsersTable() {
    const [query, setQuery] = useState({
        page: 1,
        count: 10,
        sortKey: 'firstName',
        sortOrder: 'asc',
        search: {
            firstName: '',
            lastName: '',
            email: '',
            roles: '',
            branches: '',
        },
    });

    const { isLoading, data: { data, count } = {} } = useFetchCompanyUsers(query);

    const canSort = hasLength(data);

    const debouncedOnTextChange = useMemo(
        () =>
            debounce((name, value) => {
                setQuery((prevQuery) => ({
                    ...prevQuery,
                    page: 1,
                    search: {
                        ...prevQuery.search,
                        [name]: value,
                    },
                }));
            }, 500),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        return () => {
            debouncedOnTextChange.cancel();
        };
    }, [debouncedOnTextChange]);

    const columns = useMemo(
        () => [
            {
                Header: 'First Name',
                accessor: propOrDash('firstName'),
                id: 'firstName',
                Cell: createUserLinkCell(USER_LINK_PATHS.edit.key),
                canSort: true,
                Filter: (
                    <SearchColumnFilterManual
                        name="firstName"
                        placeHolder="Filter by First Name"
                        onFilter={(e) => debouncedOnTextChange(e.target.name, e.target.value)}
                    />
                ),
                canFilter: true,
            },
            {
                Header: 'Last Name',
                accessor: propOrDash('lastName'),
                id: 'lastName',
                Cell: createUserLinkCell(USER_LINK_PATHS.edit.key),
                canSort: true,
                Filter: (
                    <SearchColumnFilterManual
                        name="lastName"
                        placeHolder="Filter by Last Name"
                        onFilter={(e) => debouncedOnTextChange(e.target.name, e.target.value)}
                    />
                ),
                canFilter: true,
            },
            {
                Header: 'Email',
                accessor: propOrDash('email'),
                id: 'email',
                Cell: createUserLinkCell(USER_LINK_PATHS.edit.key),
                canSort: true,
                Filter: (
                    <SearchColumnFilterManual
                        name="email"
                        placeHolder="Filter by Email"
                        onFilter={(e) => debouncedOnTextChange(e.target.name, e.target.value)}
                    />
                ),
                canFilter: true,
            },
            {
                Header: 'Roles',
                accessor: R.compose(
                    R.ifElse(hasLength, R.identity, R.always('--')),
                    R.join(', '),
                    R.map(R.prop('roleName')),
                    R.sortBy(R.compose(R.toLower, R.prop('roleName'))),
                    R.prop('roles')
                ),
                id: 'roles',
                Cell: RolesCell,
                Filter: (
                    <SearchColumnFilterManual
                        name="roles"
                        placeHolder="Filter by Roles"
                        onFilter={(e) => debouncedOnTextChange(e.target.name, e.target.value)}
                    />
                ),
                canFilter: true,
                canSort: true,
            },
            {
                Header: 'Branches',
                accessor: R.compose(
                    R.ifElse(hasLength, R.identity, R.always('--')),
                    R.join(', '),
                    R.map(R.prop('name')),
                    R.sortBy(R.compose(R.toLower, R.prop('name'))),
                    R.prop('branches')
                ),
                id: 'branches',
                Cell: BranchesCell,
                Filter: (
                    <SearchColumnFilterManual
                        name="branches"
                        placeHolder="Filter by Branches"
                        onFilter={(e) => debouncedOnTextChange(e.target.name, e.target.value)}
                    />
                ),
                canFilter: true,
                canSort: true,
            },
            {
                Header: 'Actions',
                id: 'actions',
                Cell: UserActionsCell,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const onPageChange = ({ pageIndex, pageSize }) => {
        setQuery((prevQuery) => ({
            ...prevQuery,
            page: pageIndex,
            count: pageSize,
        }));
    };

    const onSortChange = (sort) => {
        setQuery((prevQuery) => ({
            ...prevQuery,
            sortKey: sort.sortKey,
            sortOrder: sort.sortOrder,
        }));
    };

    return (
        <ManualPageTable
            columns={columns}
            totalRows={count}
            canSort={canSort}
            data={data}
            isLoading={isLoading}
            pageSizeOptions={[10, 25, 50]}
            onPageChange={onPageChange}
            onSortChange={onSortChange}
            currentPage={query.page}
            getKey={R.prop('id')}
            initialSortKey="firstName"
            initialSortOrder="asc"
        />
    );
}

UsersTable.propTypes = propTypes;
UsersTable.defaultProps = defaultProps;

export default UsersTable;
